import { z } from "zod";
import { slugString } from "./slug.js";
import { SIGNED_WEIGHT_KG_MAX, SIGNED_WEIGHT_KG_MIN } from "../units.js";
import { Side } from "../side.js";
// ⭐ IMPORTED, not mirrored. This file's idiom is to re-declare its own
// shapes (PlannedTargetWeight mirrors SetIntensity) because the planned
// side genuinely differs — it splits rpe/rir out into their own fields.
// The time axis does NOT differ: a bout is bounded the same way whether
// a program prescribes it or a plan carries it, so a second copy could
// only drift (§CONSOLIDAMENTO). One-way import, no cycle: nothing under
// schemas/ imports plan.js.
import { AmrapStop, SetPause, SetWork } from "./program.js";

const ExerciseSlug = slugString(64);

const SetReps = z.number().int().min(0).max(999);
// Absolute target weight allows negative so assisted variants (band or
// machine) are plannable as e.g. -20 kg. Percentage is positive only;
// "assistance as percentage of MAX" is meaningless without bodyweight.
const SetTargetKg = z
  .number()
  .min(SIGNED_WEIGHT_KG_MIN)
  .max(SIGNED_WEIGHT_KG_MAX);
const SetPctOfMax = z.number().min(0).max(200);
const SetRPE = z.number().min(1).max(10).optional();
// RIR target 0..10 (reps-in-reserve). Canonical effort prescription —
// see effortScale.ts. Mirrors targetRpe: set by program resolution when
// the slot intensity is "rir", left empty otherwise. A planned set
// carries at most one of targetRpe / targetRir (the program slot's
// intensity is a single kind).
const SetRIR = z.number().min(0).max(10).optional();

// Target weight has four shapes:
//   absolute   — the plan author typed a kg value, no MAX needed
//   percentage — the plan author typed e.g. 70% of MAX, resolved
//                client-side at "Start this plan" time against the
//                athlete's live MAX, so a MAX update before execution
//                shifts the targets without having to re-edit the plan.
//   history    — explicit "use the athlete's last-logged weight on
//                this exercise". Mirrors program.SetIntensity history;
//                the resolver looks back at plan-start, no kg encoded.
//                Distinct intent from `none` — coach is asking for a
//                fill, not leaving the cell blank.
//   none       — no weight prescription. Used by program resolution
//                when the slot's set intensity is "rpe" (RPE goes on
//                targetRpe; the weight stays empty for the athlete to
//                pick) or "none" (warmups, mobility holds, freestyle).
export const PlannedTargetWeight = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("absolute"), weightKg: SetTargetKg }),
  z.object({ kind: z.literal("percentage"), pctOfMax: SetPctOfMax }),
  z.object({ kind: z.literal("history") }),
  z.object({ kind: z.literal("none") }),
]);
export type PlannedTargetWeight = z.infer<typeof PlannedTargetWeight>;

export const PlannedSet = z.object({
  targetReps: SetReps,
  targetWeight: PlannedTargetWeight,
  targetRpe: SetRPE,
  targetRir: SetRIR,
  // AMRAP signal as structured data, not free-text in `notes`. The
  // resolver previously forced targetReps = 0 to mean "open ended" and
  // appended an "AMRAP" string to notes; that wiped the coach's
  // prescribed floor (e.g. "5+ reps") and made downstream rendering
  // ambiguous. Now `targetReps` carries the floor (the rep count the
  // coach typed, default 0 for pure freestyle) and `amrap: true` flips
  // the logger into "X+ reps" mode. Logger / planner consumers should
  // read both fields: when amrap is set, treat targetReps as a minimum.
  amrap: z.boolean().optional(),
  // #355: SetPrescription.amrapStop, on an AMRAP set only.
  amrapStop: AmrapStop.optional(),
  // #336 S3: the rep range around targetReps, from SetPrescription. Display
  // only: the logger prefills the target and shows the range beside it.
  repsMin: SetReps.optional(),
  repsMax: SetReps.optional(),
  // Time axis, carried through from SetPrescription (HI-1). Without
  // these two the prescription dies at program resolution.
  // ⚠️ Slice 1 wrote here that "PlannedSet is the ONLY thing the Logger
  // reads" — it is not, and the sentence hid a gap for a day: the Logger
  // reads a PlanStarter, and buildPlanStarter (planStart.ts) dropped both
  // fields on the floor, so the 30/90 bout DID reach the athlete as a bare
  // reps set. Slice 2 carries them across that boundary too.
  work: SetWork.optional(),
  restSec: z.number().int().min(0).max(3600).optional(),
  // HI-8: SetPrescription.pause, carried through. Display only.
  pause: SetPause.optional(),
  notes: z.string().max(200).optional(),
});
export type PlannedSet = z.infer<typeof PlannedSet>;

// Same shape as WorkoutEntryGroup — superset/circuit linkage between
// two or more entries that share a group.id. Plans declare grouping
// explicitly (no alternation detector at planning time): the plan
// author picks "Superset A" on each member entry, and the resolver
// passes the group through to the workout draft when the athlete
// starts the plan, so the logger renders the same group window.
export const PlannedEntryGroup = z.object({
  id: z.string().min(1).max(64),
  kind: z.enum(["superset", "circuit"]),
  // PE-30 — the label travels with the group all the way to the session.
  // Without it a superset the coach NAMED in the program came back as
  // "Superset N" the moment the athlete started that session: the name
  // survived the save and died at the resolver, one step further down.
  name: z.string().trim().min(1).max(60).optional(),
});
export type PlannedEntryGroup = z.infer<typeof PlannedEntryGroup>;

export const PlannedExerciseEntry = z.object({
  exerciseId: ExerciseSlug,
  // Snapshot at write time; server stamps from catalog so the athlete view
  // never depends on the catalog still containing the slug verbatim.
  exerciseName: z.string().min(1).max(120).optional(),
  sets: z.array(PlannedSet).min(1).max(50),
  notes: z.string().max(500).optional(),
  // Per-entry default for the Logger's RPE column. When true, sessions
  // started from this plan open the entry with the RPE column on; the
  // athlete can still toggle per-session (no propagation back).
  // ⚠️ The `resolveRpeDefault()` this used to name was dropped as dead
  // when the single effort column shipped (2026-05-26); the seeding now
  // goes through the effort-column path, not a resolver in notes.ts.
  rpeEnabled: z.boolean().optional(),
  // Per-entry default for the Logger's RIR column. Same propagation
  // model as rpeEnabled (plan-author-set, off by default, no back-
  // propagation from session). RIR is a more objective load gauge
  // than RPE; the Progress fatigue-drift panel reads sets that carry
  // both rpe and rir.
  rirEnabled: z.boolean().optional(),
  // Optional grouping. ≥2 entries sharing a group.id form a
  // superset/circuit; singletons are silently dropped at workout
  // start (mirrors WorkoutEntry semantics) so a half-built plan
  // doesn't leak a stray group label into the logger.
  group: PlannedEntryGroup.optional(),
  // #75: the side the program slot prescribes (Slot.side). The logger seeds
  // it on each set. Absent: nothing said.
  side: Side.optional(),
});
export type PlannedExerciseEntry = z.infer<typeof PlannedExerciseEntry>;

// Lifecycle:
//   draft     — coach is still composing, athlete row not actionable
//   scheduled — handed to the athlete, waiting for execution
//   completed — athlete saved a workout from this plan (per-row, not global)
//   skipped   — athlete or coach explicitly dismissed this row
// A followed plan has no row of its own: it shows the author's (CO-37).
export const PlanStatus = z.enum([
  "draft",
  "scheduled",
  "completed",
  "skipped",
]);
export type PlanStatus = z.infer<typeof PlanStatus>;

export const Plan = z.object({
  id: z.string().min(1),
  createdBy: z.string().min(1), // sub of the author (self or coach)
  assignedTo: z.array(z.string().min(1)).min(1).max(20),
  name: z.string().min(1).max(120),
  notes: z.string().max(500).optional(),
  // CO-17: who wrote it — the athlete themselves, or a coach on their
  // behalf. Flat next to createdBy, no provenance sub-object. Defaults
  // to "athlete" (self-authored is the common case) so every plan
  // written before this field existed still parses. Immutable after
  // creation: PATCH preserves it from the existing row
  // (plansLifecycle.ts), same as createdBy — a client cannot rewrite
  // who authored a plan after the fact.
  authoredAs: z.enum(["athlete", "coach"]).optional().default("athlete"),
  // Calendar date with no time component. "" means unscheduled (the row is
  // stored under sk=PLAN#ANY#... so it sorts after dated entries).
  scheduledFor: z.iso.date().optional(),
  entries: z.array(PlannedExerciseEntry).min(1).max(40),
  status: PlanStatus,
  // #374: the id of the session that marked this row completed; deleting
  // that session reopens the plan. Written by the server only: NewPlan
  // leaves it out, so a client body that carries it loses it.
  completedBy: z.string().min(1).optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Plan = z.infer<typeof Plan>;

// Nobody but `sub` is assigned (absent, empty or [sub]): the row is their own
// execution. The one rule for the server's flip, for keeping `completed` on
// an edit of the plan, and for the calendar's «Missed» (#499): the author's
// row of a plan with other assignees is not the author's to miss.
export const assignedToSelfOnly = (assigned: unknown, sub: string): boolean =>
  !Array.isArray(assigned) || assigned.every((s: unknown) => s === sub);

// Client → server payload. Server fills id, createdBy, createdAt, updatedAt
// and defaults status to "scheduled" when scheduledFor is present, else
// "draft".
//
// assignedToEmails (L.4) lets a coach add other athletes by email without
// knowing their Cognito sub. The server resolves each email via Cognito
// AdminListUsers and merges the result into assignedTo (deduped). If any
// email fails to resolve the request is rejected, no orphan plans.
//
// The plain assignedTo array still works for self-coaching (the common
// case): the client just sends [callerSub] and skips assignedToEmails.
// assignedTo is optional on the wire because self-coaching is the common
// case: the server fills [callerSub] when the client omits it or sends an
// empty array. The stored Plan keeps min(1) (validated at the typed
// layer), matching NewProgram's shape so the two payloads stay symmetric.
export const NewPlan = Plan.omit({
  id: true,
  createdBy: true,
  createdAt: true,
  updatedAt: true,
  assignedTo: true,
  completedBy: true,
}).extend({
  // Optional client-generated UUID. The mobile/offline-first client
  // mints the id locally so it can stage the row in IndexedDB while the
  // device is offline, then replay the POST when connectivity returns
  // and trust the server stamp matches what was already shown in the
  // UI. Server fills with randomUUID() when the client omits it.
  // Replays of the same id hit a 409 duplicate_id (idempotency).
  id: z.uuid().optional(),
  status: PlanStatus.optional(),
  assignedTo: z.array(z.string().min(1)).max(20).optional(),
  assignedToEmails: z.array(z.email()).max(20).optional(),
});
export type NewPlan = z.infer<typeof NewPlan>;

// CO-21: body for PATCH /:id/assignees — the assignees-only partial,
// twin of the full NewPlan replace-body and of ProgramAssigneesPatch.
// Field is `assigneeEmails` (not `assignedToEmails`): this endpoint
// only ever carries assignee data, never plan content.
export const PlanAssigneesPatch = z.object({
  assignedTo: z.array(z.string().min(1)).min(1).max(20),
  assigneeEmails: z.array(z.email()).max(20).optional(),
});
export type PlanAssigneesPatch = z.infer<typeof PlanAssigneesPatch>;
